import cn from 'classnames'

import { useAlertsState } from '@/components/Alertsv2/useAlertState'
import { userGroups } from '@/constants/groups'
import { useCurrentApplication } from '@/graphql/services/application'
import { useGetApplicationValuationsCalcsQuery } from '@/graphql/services/applicationCalcs'
import {
  isDisabledByAppraisalReviewStatus,
  isDisabledByExistentAlerts,
  isDisabledByExpiredDocsAlerts,
  isDisabledByNegativeNetCashProceeds,
  isStateDisabledForGroup,
  isUserInGroup,
} from '@/utils/StageSelector'
import { ApplicationState } from '@/utils/checklistGroupMappings'

interface CompletedStateMessageProps {
  viewingStage: string
  applicationStage: string
  nextGroup?: ApplicationState
  goToNextStage: () => void
  hasEscalationsNotApproved: boolean
  isLoadingApplication: boolean
  isUpdating: boolean
  isLoadingUser: boolean
  user: any
}

export const CompletedStateMessage = ({
  viewingStage,
  applicationStage,
  nextGroup,
  goToNextStage,
  hasEscalationsNotApproved,
  isLoadingApplication,
  isUpdating,
  isLoadingUser,
  user,
}: CompletedStateMessageProps) => {
  const { alerts } = useAlertsState()
  const { data: application } = useCurrentApplication()

  const { data: applicationValuationsCalcsResponse } = useGetApplicationValuationsCalcsQuery(
    { externalId: application?.externalId },
    { skip: !application?.externalId }
  )

  const valuationsCalculations =
    applicationValuationsCalcsResponse?.application?.valuationsCalculations

  const isBlockedByDocExpiredAlert = isDisabledByExpiredDocsAlerts(
    user,
    viewingStage,
    nextGroup,
    alerts
  )

  const isBlockedByExistingAlerts = isDisabledByExistentAlerts(user, nextGroup, alerts)

  const isBlockedByAppraisalStatus = isDisabledByAppraisalReviewStatus(nextGroup, {
    valuationsCalculations,
  })

  const flipToFinalApprovalUAMBlocked = isDisabledByNegativeNetCashProceeds(nextGroup, alerts)

  const isBlockedByGroup =
    isStateDisabledForGroup(nextGroup, user) &&
    !user.is_superuser &&
    !isUserInGroup(user, userGroups.LOCK_OVERRIDE)

  const isDisabled = [
    isLoadingApplication,
    isUpdating,
    hasEscalationsNotApproved,
    isLoadingUser,
    flipToFinalApprovalUAMBlocked,
    isBlockedByDocExpiredAlert,
    isBlockedByExistingAlerts,
    isBlockedByGroup,
    isBlockedByAppraisalStatus,
  ].some(Boolean)

  const getTooltipMessage = () => {
    let message = ''

    if (hasEscalationsNotApproved) {
      message = 'Approval is required for all escalations before advancing to the next sub-stage.'
    } else if (isBlockedByDocExpiredAlert) {
      message = 'A document has Expired. Please check Alerts.'
    } else if (isBlockedByExistingAlerts) {
      message = 'Open Alerts detected. Please resolve.'
    } else if (isBlockedByAppraisalStatus) {
      message = 'Valuation Review Status is not Approved'
    }

    return message
  }

  return (
    <div className='flex flex-col items-center px-6 py-4 gap-3'>
      <p>All tasks are finished and this section has been completed.</p>
      {viewingStage === applicationStage && (
        <>
          <p>Click the button below to advance to {nextGroup?.type_name}</p>
          <div
            className={cn('', { tooltip: isDisabled })}
            data-tip={getTooltipMessage()}
          >
            <button
              disabled={isDisabled}
              onClick={goToNextStage}
              className='btn btn-primary'
            >
              {isLoadingApplication || isUpdating ? 'Loading...' : 'Advance to Next Stage'}
            </button>
          </div>
        </>
      )}
    </div>
  )
}
